import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { AppState } from '../types';
import { normalizeSelectedComponents } from '../utils/componentIdMapping';
import { apiService, Site, Entity, Event, Concept, Document, EntityConnection } from '../services/apiService';
import type { RootState } from '../redux/store';

interface AppContextType {
  state: AppState;
  setSelectedSite: (siteId: string | null) => void;
  setSelectedComponents: (componentIds: string[]) => void;
  setSelectedConnections: (connectionIds: string[]) => void;
  setActiveTab: (tabId: string | null) => void;
  updateFilter: (filter: Partial<AppState['filterCriteria']>) => void;
  // Backend data
  sites: Site[];
  entities: Entity[];
  events: Event[];
  concepts: Concept[];
  documents: Document[];
  connections: EntityConnection[];
  selectedTenant: string | null;
  setSelectedTenant: (tenantId: string | null) => void;
  loading: boolean;
  error: string | null;
  refetchData: () => Promise<void>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

interface AppProviderProps {
  children: ReactNode;
  initialState?: AppState;
  onStateChange?: (state: AppState) => void;
}

export const AppProvider: React.FC<AppProviderProps> = ({
  children,
  initialState,
  onStateChange
}) => {
  // Get the user's tenant from Redux auth state (set during login from /api/v1/auth/me)
  const userTenant = useSelector((state: RootState) => state.auth.profile.tenant);

  const [state, setState] = useState<AppState>(initialState || {
    selectedSite: null, // Will be set from backend
    selectedComponents: [],
    selectedConnections: [],
    activeTab: null,
    filterCriteria: {}
  });

  // Backend data state
  const [sites, setSites] = useState<Site[]>([]);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [concepts, setConcepts] = useState<Concept[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [connections, setConnections] = useState<EntityConnection[]>([]);
  // Use the user's tenant from Redux, with local override capability
  const [tenantOverride, setTenantOverride] = useState<string | null>(null);
  const selectedTenant = tenantOverride ?? userTenant;
  const setSelectedTenant = setTenantOverride; // Allow components to override if needed
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setSelectedSite = useCallback((siteId: string | null) => {
    setState(prev => {
      const newState = { ...prev, selectedSite: siteId };
      onStateChange?.(newState);
      return newState;
    });
  }, [onStateChange]);

  const setSelectedComponents = useCallback((componentIds: string[]) => {
    setState(prev => {
      // Use actual component IDs without normalization to preserve entity names from database
      // TODO: Reactivate normalization if cross-tab filtering with legacy ID formats is needed
      // const normalizedIds = normalizeSelectedComponents(componentIds);
      // const newState = { ...prev, selectedComponents: normalizedIds };
      const newState = { ...prev, selectedComponents: componentIds };
      onStateChange?.(newState);
      return newState;
    });
  }, [onStateChange]);

  const setSelectedConnections = useCallback((connectionIds: string[]) => {
    setState(prev => {
      const newState = { ...prev, selectedConnections: connectionIds };
      onStateChange?.(newState);
      return newState;
    });
  }, [onStateChange]);

  const setActiveTab = useCallback((tabId: string | null) => {
    setState(prev => {
      const newState = { ...prev, activeTab: tabId };
      onStateChange?.(newState);
      return newState;
    });
  }, [onStateChange]);

  const updateFilter = useCallback((filter: Partial<AppState['filterCriteria']>) => {
    setState(prev => {
      const newState = {
        ...prev,
        filterCriteria: { ...prev.filterCriteria, ...filter }
      };
      onStateChange?.(newState);
      return newState;
    });
  }, [onStateChange]);

  // Fetch data from backend
  const refetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Test health endpoint first to confirm backend is accessible
      const healthCheck = await apiService.healthCheck();

      // Fetch sites first (pass tenant ID to get user's portfolios/sites)
      try {
        const sitesData = await apiService.getSites(selectedTenant || undefined);
        setSites(sitesData);

        // Don't reset selectedSite here - let Redux manage it globally
        // Only use it if it's already set, otherwise let Redux/User Profile handle the selection
        let siteToUse = state.selectedSite;

        // Only auto-select first site if absolutely no site is selected AND Redux doesn't have one
        // This prevents overriding the user's active site selection
        // The Redux store and Sidebar will handle active site initialization from user profile

        // Get portfolio_id from the selected site (needed for microstation API)
        const selectedSiteData = sitesData.find(s => s.site_id === siteToUse);
        const portfolioId = selectedSiteData?.portfolio_id;

        // Fetch entities and connections (events, concepts, documents not available in microstation API)
        const [entitiesData, connectionsData] = await Promise.all([
          apiService.getEntities(siteToUse || undefined, selectedTenant || undefined, portfolioId || undefined),
          apiService.getConnections(siteToUse || undefined, portfolioId || undefined)
        ]);

        setEntities(entitiesData);
        setConnections(connectionsData);
        // These endpoints don't exist in microstation - set empty
        setEvents([]);
        setConcepts([]);
        setDocuments([]);
      } catch (apiError) {
        console.warn('Backend API endpoints are not working (500 errors), likely database not set up:', apiError);
        // Set empty data but don't fail the whole app
        setSites([]);
        setEntities([]);
        setEvents([]);
        setConcepts([]);
        setDocuments([]);
        setConnections([]);
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch data';
      setError(errorMessage);
      console.error('Error fetching backend data:', err);
    } finally {
      setLoading(false);
    }
  }, [state.selectedSite, selectedTenant]);

  // Effect to load initial data
  useEffect(() => {
    refetchData();
  }, [refetchData]);



  return (
    <AppContext.Provider value={{
      state,
      setSelectedSite,
      setSelectedComponents,
      setSelectedConnections,
      setActiveTab,
      updateFilter,
      sites,
      entities,
      events,
      concepts,
      documents,
      connections,
      selectedTenant,
      setSelectedTenant,
      loading,
      error,
      refetchData
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return context;
};